import { useCallback, useEffect } from 'react'

import { startCase } from 'lodash'

import { type AlertProps } from './interfaces'

import Alert from '@mui/material/Alert'
import Box from '@mui/material/Box'
import Link from '@mui/material/Link'
import Typography from '@mui/material/Typography'

const AlertStyled = ({
  alert,
  onAlertClose,
  shortFormat: _shortFormat = false,
}: {
  alert: AlertProps
  onAlertClose: (alert: AlertProps) => void
  shortFormat?: boolean
}) => {
  const { items, action } = alert
  const timeoutDefault = alert.closeOnTimeout ? 5000 : undefined

  const handleAlertClose = useCallback(
    (alert: AlertProps) => {
      onAlertClose(alert)
    },
    [onAlertClose]
  )

  // Close on timeout
  useEffect(() => {
    if (alert.closeOnTimeout && timeoutDefault) {
      const timeout = setTimeout(() => {
        onAlertClose(alert)
      }, alert.closeOnTimeoutOverride || timeoutDefault)

      return () => {
        clearTimeout(timeout)
      }
    }
  }, [alert, onAlertClose, timeoutDefault])

  return (
    <Alert
      aria-label={`${startCase(alert.severity)} notification`}
      severity={alert.severity}
      onClose={() => {
        handleAlertClose(alert)
      }}
      sx={{ maxHeight: 150 }}
    >
      <Typography>{alert.message}</Typography>
      {items && (
        <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
          {items.map((item, index) => (
            <Typography component="li" key={item}>
              {item}
              {index === items.length - 1 && action && (
                <>
                  {' '}
                  <Link sx={{ cursor: 'pointer' }} onClick={action.onClick}>
                    {action.label}
                  </Link>
                </>
              )}
            </Typography>
          ))}
        </Box>
      )}
      {!items && action && (
        <Typography>
          <Link sx={{ cursor: 'pointer' }} onClick={action.onClick}>
            {action.label}
          </Link>
        </Typography>
      )}
    </Alert>
  )
}

export default AlertStyled
